import { GPUParticleSystem, ParticleSystem, Vector3 } from '@babylonjs/core';
import { Level } from '../Level';
import { ObstacleDistributionConfig } from '../ObstacleGenerator';
import { ObstacleBehavior } from './ObstacleBehavior';

/**
 * Wind Zone Behavior – pushes the player horizontally when within windRadius.
 *
 * Config fields:
 *   windForce    – lateral force applied per frame (default 6)
 *   windRadius   – trigger radius (default 8)
 *   axis         – primary wind axis: 'x' or 'z' (reused, default 'x')
 *   direction    – +1 or -1 along axis (reused, default 1)
 *   speed        – particle system wind speed hint (default 1)
 */
export class ObstacleWindZoneBehavior extends ObstacleBehavior {
  private windParticles: ParticleSystem | GPUParticleSystem | null = null;
  private windDirection: Vector3 = Vector3.Zero();

  constructor(startPosition: Vector3, pathIndex: number, config: ObstacleDistributionConfig) {
    super('obstacleWindZone', startPosition, pathIndex, config);
  }

  initLevel(level: Level): ObstacleWindZoneBehavior {
    super.initLevel(level);

    const dir = this.config.direction ?? 1;
    const axis = this.config.axis ?? 'z';
    switch (axis) {
      case 'x':
        this.windDirection = new Vector3(dir, 0, 0);
        break;
      case 'y':
        this.windDirection = new Vector3(0, dir, 0);
        break;
      case 'z':
        this.windDirection = new Vector3(0, 0, dir);
        break;
    }

    // Wind particle system
    this.windParticles = this.level.particles.createWindSystem(this.mesh, this.windDirection);

    return this;
  }

  update(deltaTime: number): void {
    if (!this.level?.player) return;

    const radius = this.config.windRadius ?? 18;
    const force = this.config.windForce ?? 0.2;
    const dist = this.getPlayerDistance();

    if (dist < radius) {
      const pathInfo = this.level.getPathInfo(this.pathIndex);
      const falloff = 1 - dist / radius;
      const windVelocity = this.windDirection.add(pathInfo.lateral).scale(force * falloff);
      this.level.player.applyExternalImpulse(windVelocity);
    }
  }

  detach(): void {
    this.windParticles?.dispose();
    this.windParticles = null;
    super.detach();
  }

  clone(): ObstacleWindZoneBehavior {
    return new ObstacleWindZoneBehavior(this.startPosition, this.pathIndex, this.config);
  }
}
